import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { CONSTANTS } from 'src/app/shared/utils/constants';
import { switchMap, take } from 'rxjs/operators';
import { Observable, combineLatest } from 'rxjs';
import { ConstantsRealTimeSegments } from 'src/app/shared/utils/costants-real-time-segments';
import { TriggerInterface } from 'src/app/core/interfaces/trigger.interface';
import { PushSubscriptionInterface } from '../interfaces/push-subscription.interface';

@Injectable({
    providedIn: 'root',
})
export class CacheService {
    constructor(private readonly httpClient: HttpClient) {}


    deleteCacheForTriggerApp(mediaId: string): Observable<any> {
        const DELETE_CACHE_TRIGGER_URL = CONSTANTS.API_REST_DELETE_TRIGGER_APP.replace(':mediaId', mediaId);
        return this.httpClient.delete<any>(DELETE_CACHE_TRIGGER_URL).pipe(take(1));
    }

    deleteCacheForRealTimeSegmentsApp(mediaId: string): Observable<any> {
        const DELETE_CACHE_REAL_TIME_SEGMENT_URL = CONSTANTS.API_REST_DELETE_REAL_TIME_SEGMENT_APP.replace(':mediaId', mediaId);
        return this.httpClient.delete<any>(DELETE_CACHE_REAL_TIME_SEGMENT_URL).pipe(take(1));
    }

    deleteSegmentsForTriggerCache(mediaId: string): Observable<any> {
      const params = new HttpParams().set('mediaId', mediaId);
      return this.httpClient.delete(ConstantsRealTimeSegments.deleteTriggersSegmentsCacheUrl, { params }).pipe(take(1));
    }

    updateSegmentVersion(mediaId: string): Observable<any> {
      const versionUrl = CONSTANTS.API_REST_UPDATE_SEGMENT_VERSION.replace(':mediaId', mediaId);
      return this.httpClient.post<{ message: string }>(versionUrl, {}).pipe(take(1));
    }

    updateTriggerVersion(media: string): Observable<{ message: string }> {
        const versionUrl = CONSTANTS.API_REST_UPDATE_TRIGGER_VERSION.replace(':mediaId', media);
        return this.httpClient.post<{ message: string }>(versionUrl, {}).pipe(take(1));
    }

    updateSegmentsCacheForTriggers(mediaId: string): Observable<any> {
      return this.deleteSegmentsForTriggerCache(mediaId).pipe(
        switchMap(() => this.updateSegmentVersion(mediaId))
      );
    }

    triggersCacheActivationProcessor(triggerId: string, collection: string): Observable<any>{
      const url = CONSTANTS.TRIGGER_ACTIVATION_URL;
      const data = {
        triggerId,
        collection
      };
      return this.httpClient.post(url, data).pipe(take(1));
    }

    triggersCacheInactivationProcessor(triggerId: string, collection: string): Observable<any>{
      const url = CONSTANTS.TRIGGER_INACTIVATION_URL;
      const data = {
        triggerId,
        collection
      };
      return this.httpClient.post(url, data).pipe(take(1));
    }

    triggersPurgeScheduledJobs(triggerId: string, collection: string): Observable<any>{
      const url = CONSTANTS.TRIGGER_PURGE_SCHEDULE_JOBS_URL;
      const data = {
        triggerId,
        collection
      };
      return this.httpClient.post(url, data).pipe(take(1));
    }

    triggersCacheProcessors(trigger: TriggerInterface | PushSubscriptionInterface, collection: string, hasPeriodicScheduleChanged = true) {
      if(trigger.programDates.length && trigger.hasPeriodicSchedule && hasPeriodicScheduleChanged){
        return combineLatest([
          this.triggersCachePunctualProcessor(trigger.id, collection),
          this.triggersCachePeriodicProcessor(trigger.id, collection)
        ]);
      }
      if(trigger.hasPeriodicSchedule && hasPeriodicScheduleChanged){
        return combineLatest([
          this.triggersCacheInactivationProcessor(trigger.id, collection),
          this.triggersCachePeriodicProcessor(trigger.id, collection)
        ]);
      }
      if(trigger.programDates.length){
        return this.triggersCachePunctualProcessor(trigger.id, collection);
      }
      if(trigger.active) return this.triggersCacheActivationProcessor(trigger.id, collection);
      if(!trigger.active) return this.triggersCacheInactivationProcessor(trigger.id, collection);
    }

    triggersCachePunctualProcessor(triggerId: string, collection: string): Observable<any>{
      const url = CONSTANTS.TRIGGER_PUNCTUAL_TASK_URL;
      const data = {
        triggerId,
        collection,
        operation: CONSTANTS.TRIGGER_TAG_TASK,
      };
      return this.httpClient.post(url, data).pipe(take(1));
    }

    triggersCachePeriodicProcessor(triggerId: string, collection: string): Observable<any>{
      const url = CONSTANTS.TRIGGER_PERIODIC_TASK_URL;
      const data = {
        triggerId,
        collection,
        operation: CONSTANTS.TRIGGER_TAG_TASK
      };
      return this.httpClient.post(url, data).pipe(take(1));
    }
}
